













































import Vue from "vue";
import ModalDlg from "@/components/ModalDlg.vue";
import { Cropper } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
import pica from "pica";
import { useStore } from "@/store/store";
import { mapStores } from "pinia";
import { BvModalEvent } from "bootstrap-vue";
import {debounce} from "lodash";
import {isMacOSPlatform} from "@/helpers/common";

const picaInstance = pica();

export default Vue.extend({
    name: "EditImageDlg",

    components:{
        Cropper,
        ModalDlg,
    },

    props:{
        dlgId: String,
        dlgTitle: String,
        imgToEdit: String, /* Base 64 string */
        showImgPreview:{type: Function}, /* Takes new base64 string as input, null to cancel preview */
    },
    
    data: function() {
        return {
            originalImgSize: "Loading...",
            currentImgSize: "Loading...",
            imageScale: 100,
            cropSize: {left: 0, top: 0, width: 1, height: 1},
            cachedCanvas: undefined as CanvasRenderingContext2D | undefined,
            cursorPos: undefined as string | undefined,
            cursorColor: undefined as string | undefined,
        };
    },

    created() {
        // Register the event listener for the dialog here
        this.$root.$on("bv::modal::hide", this.onHideModalDlg);
        this.updatePreview = debounce(this.updatePreview, 500);
    },

    beforeDestroy(){
        // Remove the event listener for the dialog here, just in case...
        this.$root.$off("bv::modal::hide", this.onHideModalDlg);
    },
    
    mounted() {
        window.addEventListener("keydown", this.onKeyDown);
    },

    destroyed() {
        window.removeEventListener("keydown", this.onKeyDown);
    },

    computed:{
        ...mapStores(useStore),

        dlgMsg(): string{
            return this.appStore.simpleModalDlgMsg;
        },
    },

    methods:{
        isMacOSPlatform,
        onHideModalDlg(event: BvModalEvent, id: string){
            this.showImgPreview(null);
        },
        updatePreview() {
            this.getUpdatedMedia().then((m) => this.showImgPreview(/"([^"]+)"/.exec(m.code)?.[1] ?? "")).catch((err) => {});
        },
        defaultSize({imageSize, visibleArea} : { imageSize: {width: number, height: number}, visibleArea : {width: number, height: number} }) {
            return {
                width: (visibleArea || imageSize).width,
                height: (visibleArea || imageSize).height,
            };
        },
        change(info : {image: any, coordinates: {left: number, top: number, width: number, height: number}}) {
            this.cropSize = info.coordinates; 
            this.updateCurrentSize();
            this.updatePreview();
        },
        imageLoaded() {
            // We have to go fetch the image from the document and ask its size:
            let img = document.getElementsByClassName("edit-image-cropper-image")?.[0] as HTMLImageElement | null;
            if (img) {
                this.originalImgSize = img.naturalWidth + " × " + img.naturalHeight;
                this.cropSize = {left: 0, top: 0, width: img.naturalWidth, height: img.naturalHeight};
                // Set scale so that image is under 1000 pixels:
                if (img.naturalWidth > 1000 || img.naturalHeight > 1000) {
                    this.imageScale = Math.floor(100 * Math.min(1000 / img.naturalWidth, 1000 / img.naturalHeight));
                }
                else {
                    this.imageScale = 100;
                }
                this.updateCurrentSize();
            }
            else {
                // Shouldn't happen, but handle it gracefully in case:
                this.originalImgSize = "Unknown";
                this.currentImgSize = this.originalImgSize;
            }            
        },
        updateCurrentSize() {
            const scale = this.imageScale / 100.0;
            this.currentImgSize = Math.ceil(this.cropSize.width * scale) + " × " + Math.ceil(this.cropSize.height * scale);
            this.updatePreview();
        },
        getUpdatedMedia() : Promise<{code: string, mediaType: string}> {
            const { canvas } = (this.$refs.cropper as Cropper).getResult();
            if (!canvas) {
                return Promise.reject("Loading");
            }
            const scale = this.imageScale / 100.0;
            let width = this.cropSize.width * scale;
            let height = this.cropSize.height * scale;

            const targetCanvas = document.createElement("canvas");
            targetCanvas.width = width;
            targetCanvas.height = height;
            // Skip resize if not needed:
            return (this.imageScale == 100 ? Promise.resolve(canvas.toDataURL()) :
                picaInstance.resize(canvas, targetCanvas, {
                    unsharpAmount: 80,
                    unsharpRadius: 0.6,
                    unsharpThreshold: 2,
                }).then((c) => c.toDataURL("png"))).then((resized) => {
                return Promise.resolve({code: "load_image(\"" + resized + "\")", mediaType: "image/png"});
            });
        },
        handleMouseMove(event: MouseEvent) {
            const cropper = this.$refs.cropper as Cropper;
            const imageElement = cropper?.$el.querySelector("img");

            if (cropper && imageElement && imageElement.complete) {
                const rect = imageElement.getBoundingClientRect();
                const offsetX = event.clientX - rect.left;
                const offsetY = event.clientY - rect.top;

                const scaleX = imageElement.width / imageElement.getBoundingClientRect().width;
                const scaleY = imageElement.height / imageElement.getBoundingClientRect().height;

                const imageX = Math.floor(offsetX * scaleX);
                const imageY = Math.floor(offsetY * scaleY);
                
                if (imageX >= 0 && imageX < imageElement.width && imageY >= 0 && imageY < imageElement.height) {
                    this.cursorPos = `(${imageX}, ${imageY})`;
                    const rgba = this.getPixelColor(imageElement, imageX, imageY);
                    this.cursorColor = `Color(${rgba[0]}, ${rgba[1]}, ${rgba[2]}, ${rgba[3]})`;
                }
                else {
                    this.cursorPos = undefined;
                    this.cursorColor = undefined;
                }
            }
        },

        getPixelColor(imgElement : HTMLImageElement, x : number, y : number) : Uint8ClampedArray {
            if (!this.cachedCanvas) {
                // Create canvas
                const canvas = document.createElement("canvas");
                canvas.width = imgElement.naturalWidth;
                canvas.height = imgElement.naturalHeight;
                this.cachedCanvas = canvas.getContext("2d") as CanvasRenderingContext2D;
                // Draw the image at original size
                this.cachedCanvas.drawImage(imgElement, 0, 0);
            }
            return this.cachedCanvas.getImageData(x, y, 1, 1).data;
        },
        
        onKeyDown(event: KeyboardEvent) {
            if (this.$refs.cropper && event.key === "c" && ((isMacOSPlatform() && event.metaKey) || (!isMacOSPlatform() && event.ctrlKey))) {
                if (event.shiftKey) {
                    if (this.cursorColor !== undefined) {
                        navigator.clipboard.writeText(this.cursorColor);
                    }
                }
                else {
                    if (this.cursorPos !== undefined) {
                        // We don't copy the outer brackets:
                        navigator.clipboard.writeText(this.cursorPos.replaceAll(/[()]/g, ""));
                    }
                }
                event.stopPropagation();
                event.stopImmediatePropagation();
                event.preventDefault();
            }
        },
    },
    watch: {
        imageScale() {
            this.updateCurrentSize();
        },
        imgToEdit() {
            // When image changes (dialog being re-shown), reset scale:
            this.imageScale = 100;
        },
    },
});
